import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { IconButton, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SydButton from '../../../../../../commonDesign/Button'
import Icon from '../../../../../../atoms/Icon'

const useStyles = makeStyles({
  section: {
    marginTop: 12
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#424242',
    marginBottom: 8
  },
  dateRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
    marginBottom: 8
  },
  dateFields: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  inlineRow: {
    display: 'flex',
    gap: 6
  }
})

function StartForm ({ configuration, onChange }) {
  const classes = useStyles()
  const dates = useMemo(() => configuration?.dates ?? {}, [configuration?.dates])
  const dateEntries = Object.entries(dates)

  const handleDateChange = useCallback((key, field, value) => {
    const updatedDates = {
      ...dates,
      [key]: { ...dates[key], [field]: value }
    }
    onChange({ ...configuration, dates: updatedDates })
  }, [dates, configuration, onChange])

  const handleKeyChange = useCallback((oldKey, newKey) => {
    if (newKey === oldKey || !newKey) return
    const updated = { ...dates }
    updated[newKey] = updated[oldKey]
    delete updated[oldKey]
    onChange({ ...configuration, dates: updated })
  }, [dates, configuration, onChange])

  const handleAddDate = useCallback(() => {
    const key = `date_${Object.keys(dates).length}`
    const updated = {
      ...dates,
      [key]: { period: 'day', point: 'end', offset: 0, calendarType: 'business', asOfType: 'maxAccountDate' }
    }
    onChange({ ...configuration, dates: updated })
  }, [dates, configuration, onChange])

  const handleRemoveDate = useCallback((key) => {
    const updated = { ...dates }
    delete updated[key]
    onChange({ ...configuration, dates: updated })
  }, [dates, configuration, onChange])

  return (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>Date Configuration</div>
      {dateEntries.map(([key, spec]) => (
        <div key={key} className={classes.dateRow}>
          <div className={classes.dateFields}>
            <TextField
              label='Key'
              value={key}
              size='small'
              variant='outlined'
              onBlur={(e) => handleKeyChange(key, e.target.value)}
              fullWidth
            />
            <div className={classes.inlineRow}>
              <TextField
                label='Period'
                select
                value={spec?.period ?? 'day'}
                size='small'
                variant='outlined'
                onChange={(e) => handleDateChange(key, 'period', e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value='day'>Day</option>
                <option value='month'>Month</option>
                <option value='quarter'>Quarter</option>
                <option value='year'>Year</option>
              </TextField>
              <TextField
                label='Point'
                select
                value={spec?.point ?? 'end'}
                size='small'
                variant='outlined'
                onChange={(e) => handleDateChange(key, 'point', e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value='start'>Start</option>
                <option value='end'>End</option>
              </TextField>
            </div>
            <div className={classes.inlineRow}>
              <TextField
                label='Offset'
                type='number'
                value={spec?.offset ?? 0}
                size='small'
                variant='outlined'
                onChange={(e) => handleDateChange(key, 'offset', parseInt(e.target.value, 10) || 0)}
              />
              <TextField
                label='Calendar'
                select
                value={spec?.calendarType ?? 'business'}
                size='small'
                variant='outlined'
                onChange={(e) => handleDateChange(key, 'calendarType', e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value='business'>Business</option>
                <option value='calendar'>Calendar</option>
              </TextField>
            </div>
          </div>
          <IconButton size='small' onClick={() => handleRemoveDate(key)} aria-label='remove date'>
            <Icon name='close' customSize='1rem' />
          </IconButton>
        </div>
      ))}
      <SydButton size='sm' variant='outline' icon='add' onClick={handleAddDate}>
        Add Date
      </SydButton>
    </div>
  )
}

StartForm.propTypes = {
  configuration: PropTypes.object,
  onChange: PropTypes.func.isRequired
}

export default StartForm
